import React, { useState, useEffect } from "react";
import api from "@api";
import {
  NavLink,
  useParams,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import "./PlayerAuth.css";
import DefaultImage from "../../../../../images/default_image.png";

function PlayerAuth() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dominantHand: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  // Player videos
  const [videos, setVideos] = useState([]);
  const [videoForm, setVideoForm] = useState({
    video_url: "",
    title: "",
    description: "",
  });
  const [videoLoading, setVideoLoading] = useState(false);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const domain = searchParams.get("domain");




  
  const fetchPlayer = async () => {
    try {
      let response;

      if (isAuthenticated) {
        response = await api.get(`/api/playersv2/${id}/information`, {
          withCredentials: true,
        });
      } else if (domain) {
        response = await api.get(
          `/api/playersv2/${id}/information?domain=${domain}`
        );
      } else {
        return setError("Unauthorized access");
      }

      setPlayer(response.data);
      if (isAuthenticated) {
        await fetchPlayerRegistrations(response.data);
      }
      const dominantHandValue =
        response.data?.attributeValues?.find(
          (item) => item?.attribute?.name === "Dominant Hand"
        )?.value || "";

      setFormData({
        firstName: response.data.firstName || "",
        lastName: response.data.lastName || "",
        email: response.data.email || "",
        phoneNumber: response.data.phoneNumber || "",
        dominantHand: dominantHandValue,
      });
    } catch (error) {
      console.error(
        "Error fetching player:",
        error.response?.data || error.message
      );
      setError("Failed to fetch player");
    }
  };


  const fetchPlayerVideos = async () => {
    try {
      const response = await api.get(`/api/players/${id}/videos`, {
        withCredentials: true,
      });

      setVideos(response.data.videos || []);
    } catch (error) {
      console.error(
        "Error fetching player videos:",
        error.response?.data || error.message
      );
    }
  };



  useEffect(() => {
    if (!loading) {
      fetchPlayer();
      fetchPlayerVideos();
    }
  }, [id, domain, isAuthenticated, loading]);

  const getPlayerName = () => {
    return `${player?.firstName || ""} ${player?.lastName || ""}`
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleRegistrationChange = async (seasonId, isRegistered) => {
    try {
      await api.patch(
        `/api/player-registrations/${id}`,
        {
          season_id: Number(seasonId),
          is_registered: isRegistered,
        },
        { withCredentials: true }
      );

      setPlayerRegistrations((prev) => ({
        ...prev,
        [seasonId]: isRegistered,
      }));
    } catch (err) {
      console.error("Error updating registration:", err);
      alert("Failed to update registration");
    }
  };



  const fetchPlayerRegistrations = async (playerData) => {
    const rosters = playerData.rosters || [];

    const seasons = [
      ...new Map(
        rosters
          .map((r) => r.seasons_leagues_team?.seasons_league?.season)
          .filter((s) => s?.id)
          .map((s) => [s.id, s])
      ).values(),
    ];

    const statuses = {};

    for (const season of seasons) {
      try {
        const res = await api.get("/api/player-registrations", {
          params: {
            organization_id: season.organization_id ?? playerData.organization_id,
            season_id: season.id,
          },
          withCredentials: true,
        });

        const registration = res.data.players.find(
          (p) => Number(p.id) === Number(playerData.id)
        );

        statuses[season.id] = registration?.is_registered ?? true;
      } catch (err) {
        console.error("Error fetching season registration:", err);
      }
    }

    setPlayerRegistrations(statuses);
  };


  const getRosterEntries = () => {
    return player?.rosters || [];
  };

  const getIndividualEntries = () => {
    return player?.seasonLeaguePlayers || [];
  };

  const handleDelete = async () => {
    const rosterCount = getRosterEntries().length;
    const individualCount = getIndividualEntries().length;

    if (rosterCount > 0 || individualCount > 0) {
      alert(
        "This player has league history. Join this player instead of deleting."
      );
      return;
    }

    if (!window.confirm(`Delete unused player "${getPlayerName()}"?`)) return;

    try {
      await api.delete(`/api/playersv2/${id}`, { withCredentials: true });
      alert("Unused player deleted");
      navigate(-1);
    } catch (error) {
      console.error(
        "Error deleting player:",
        error.response?.data || error.message
      );
      alert(error.response?.data?.message || "Failed to delete player");
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0] || null);
    setRemoveImage(false);
  };

  const handleRemoveImageClick = () => {
    setImageFile(null);
    setRemoveImage(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setImageFile(null);
    setRemoveImage(false);

    const dominantHandValue =
      player?.attributeValues?.find(
        (item) => item?.attribute?.name === "Dominant Hand"
      )?.value || "";

    setFormData({
      firstName: player.firstName || "",
      lastName: player.lastName || "",
      email: player.email || "",
      phoneNumber: player.phoneNumber || "",
      dominantHand: dominantHandValue,
    });
  };

  const handleSave = async () => {
    try {
      const data = new FormData();

      data.append("firstName", formData.firstName);
      data.append("lastName", formData.lastName);
      data.append("email", formData.email);
      data.append("phoneNumber", formData.phoneNumber);
      data.append("removeImage", removeImage ? "true" : "false");

      if (imageFile) {
        data.append("image", imageFile);
      }

      await api.put(`/api/playersv2/${id}`, data, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      await api.put(
        `/api/playersv2/${id}/attributes/1`,
        {
          value: formData.dominantHand || null,
        },
        {
          withCredentials: true,
        }
      );

      await fetchPlayer();

      setIsEditing(false);
      setImageFile(null);
      setRemoveImage(false);

      alert("Player updated successfully");
    } catch (error) {
      console.error(
        "Error updating player:",
        error.response?.data || error.message
      );

      alert("Failed to update player");
    }
  };

  const handleVideoChange = (e) => {
    setVideoForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddVideo = async () => {
    if (!videoForm.video_url.trim()) {
      alert("Please enter a video URL");
      return;
    }

    try {
      setVideoLoading(true);

      await api.post(
        `/api/players/${id}/videos`,
        {
          video_url: videoForm.video_url.trim(),
          title: videoForm.title.trim() || null,
          description: videoForm.description.trim() || null,
        },
        {
          withCredentials: true,
        }
      );

      setVideoForm({
        video_url: "",
        title: "",
        description: "",
      });

      await fetchPlayerVideos();

      alert("Video added successfully");
    } catch (error) {
      console.error(
        "Error adding player video:",
        error.response?.data || error.message
      );

      alert(error.response?.data?.message || "Failed to add video");
    } finally {
      setVideoLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Remove this video?")) return;

    try {
      await api.delete(`/api/players/${id}/videos/${videoId}`, {
        withCredentials: true,
      });

      await fetchPlayerVideos();
    } catch (error) {
      console.error(
        "Error deleting player video:",
        error.response?.data || error.message
      );

      alert(error.response?.data?.message || "Failed to delete video");
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url);

      // youtube.com/watch?v=VIDEO_ID
      if (parsedUrl.hostname.includes("youtube.com")) {
        const videoId = parsedUrl.searchParams.get("v");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }

        // youtube.com/shorts/VIDEO_ID
        if (parsedUrl.pathname.includes("/shorts/")) {
          const videoId = parsedUrl.pathname.split("/shorts/")[1]?.split("/")[0];

          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
      }

      // youtu.be/VIDEO_ID
      if (parsedUrl.hostname === "youtu.be") {
        const videoId = parsedUrl.pathname.replace("/", "");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  if (error) {
    return (
      <div className="playerIdentityAuth-page">
        <p className="playerIdentityAuth-error">{error}</p>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="playerIdentityAuth-page">
        <p className="playerIdentityAuth-loading">Loading player...</p>
      </div>
    );
  }

  const rosterEntries = getRosterEntries();
  const isRacquetSport =
    Number(player?.organization_id) === 20 ||
    player?.rosters?.some((roster) =>
      ["tennis", "pickleball", "badminton"].includes(
        roster.seasons_leagues_team?.seasons_league?.season?.sport?.type?.toLowerCase()
      )
    ) ||
    player?.seasonLeaguePlayers?.some((entry) =>
      ["tennis", "pickleball", "badminton"].includes(
        entry.seasons_league?.season?.sport?.type?.toLowerCase()
      )
    ) ||
    false;
  const individualEntries = getIndividualEntries();
  const totalPlayerEntries = rosterEntries.length + individualEntries.length;

  const showRosterHistory = rosterEntries.length > 0;
  const showIndividualHistory = individualEntries.length > 0;

  const showJerseyColumn = rosterEntries.some((r) => r.jersey_number);
  const showPositionColumn = rosterEntries.some((r) => r.position);
  const playerImage = removeImage
    ? DefaultImage
    : player.image
      ? `${player.image}?${new Date().getTime()}`
      : DefaultImage;

  return (
    <div className="playerIdentityAuth-page">
      <div className="playerIdentityAuth-profileCard">
        <div className="playerIdentityAuth-imageWrap">
          <img
            src={playerImage}
            alt={getPlayerName()}
            className="playerIdentityAuth-image"
          />
        </div>

        <div className="playerIdentityAuth-main">
          <p className="playerIdentityAuth-eyebrow">Player Record</p>

          {!isEditing ? (
            <>
              <h2 className="playerIdentityAuth-playerName">
                {getPlayerName() || "Unnamed Player"}
              </h2>

              <div className="playerIdentityAuth-actions">
                <button
                  type="button"
                  className="playerIdentityAuth-primaryBtn"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Player
                </button>

                {totalPlayerEntries === 0 && (
                  <button
                    type="button"
                    className="playerIdentityAuth-dangerBtn"
                    onClick={handleDelete}
                  >
                    Delete Unused
                  </button>
                )}
              </div>

              {totalPlayerEntries > 0 && (
                <p className="playerIdentityAuth-safeNote">
                  This player has league history. Join duplicate players instead of deleting.
                </p>
              )}
            </>
          ) : (
            <div className="playerIdentityAuth-editForm">
              <div className="playerIdentityAuth-formGrid">
                <label>
                  First Name
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                  />
                </label>

                <label>
                  Last Name
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                  />
                </label>

                <label>
                  Phone
                  <input
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="Phone Number"
                  />
                </label>
                  {isRacquetSport && (
                    <label>
                      Dominant Hand
                      <select
                        name="dominantHand"
                        value={formData.dominantHand}
                        onChange={handleChange}
                      >
                        <option value="">Select Hand</option>
                        <option value="Right">Right</option>
                        <option value="Left">Left</option>
                      </select>
                    </label>
                  )}
              </div>

              <div className="playerIdentityAuth-fileBox">
                <label>
                  Player Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={removeImage}
                  />
                </label>

                {player.image && !removeImage && (
                  <button
                    type="button"
                    className="playerIdentityAuth-secondaryBtn"
                    onClick={handleRemoveImageClick}
                  >
                    Remove Current Image
                  </button>
                )}

                {removeImage && (
                  <p className="playerIdentityAuth-safeNote">
                    Current image will be removed after saving.
                  </p>
                )}
              </div>

              <div className="playerIdentityAuth-actions">
                <button
                  type="button"
                  className="playerIdentityAuth-primaryBtn"
                  onClick={handleSave}
                >
                  Save Changes
                </button>

                <button
                  type="button"
                  className="playerIdentityAuth-secondaryBtn"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {isRacquetSport && (
      <div className="playerIdentityAuth-section playerIdentityAuth-mediaSection">
        <div className="playerIdentityAuth-sectionHeader">
          <div>
            <h3>Player Media</h3>
            <p>Add video highlights for this player.</p>
          </div>

          <div className="playerIdentityAuth-countBox">
            <span>{videos.length}</span>
            <small>Videos</small>
          </div>
        </div>

        {isAuthenticated && (
          <div className="playerIdentityAuth-videoForm">
            <label>
              YouTube Video URL
              <input
                type="url"
                name="video_url"
                value={videoForm.video_url}
                onChange={handleVideoChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>

            <label>
              Title
              <input
                type="text"
                name="title"
                value={videoForm.title}
                onChange={handleVideoChange}
                placeholder="Week 3 Highlights"
              />
            </label>

            <label>
              Description
              <textarea
                name="description"
                value={videoForm.description}
                onChange={handleVideoChange}
                placeholder="Forehand winner, long rally..."
                rows="3"
              />
            </label>

            <button
              type="button"
              className="playerIdentityAuth-primaryBtn"
              onClick={handleAddVideo}
              disabled={videoLoading}
            >
              {videoLoading ? "Adding..." : "Add Video"}
            </button>
          </div>
        )}

        {videos.length > 0 ? (
          <div className="playerIdentityAuth-videoGrid">
            {videos.map((video) => {
              const embedUrl = getYouTubeEmbedUrl(video.video_url);

              return (
                <div
                  key={video.id}
                  className="playerIdentityAuth-videoCard"
                >
                  <div className="playerIdentityAuth-videoPreview">
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        title={video.title || `Player Video ${video.id}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <a
                        href={video.video_url}
                        target="_blank"
                        rel="noreferrer"
                        className="playerIdentityAuth-videoLink"
                      >
                        Open Video
                      </a>
                    )}
                  </div>

                  <div className="playerIdentityAuth-videoInfo">
                    <h4>{video.title || "Player Highlight"}</h4>

                    {video.description && (
                      <p>{video.description}</p>
                    )}

                    {isAuthenticated && (
                      <button
                        type="button"
                        className="playerIdentityAuth-dangerTextBtn"
                        onClick={() => handleDeleteVideo(video.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="playerIdentityAuth-muted">
            No videos added yet.
          </p>
        )}
      </div>
      )}



      <div className="playerIdentityAuth-historyGrid">
        {showRosterHistory && (
          <div className="playerIdentityAuth-section">
            <div className="playerIdentityAuth-sectionHeader">
              <div>
                <h3>Team League History</h3>
                <p>Team-based leagues where this player is part of a roster.</p>
              </div>

              <div className="playerIdentityAuth-countBox">
                <span>{rosterEntries.length}</span>
                <small>Team Entries</small>
              </div>
            </div>

            <div className="playerIdentityAuth-tableWrap">
              <table className="playerIdentityAuth-table">
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Season</th>
                    <th>League</th>
                    <th>Registration</th>
                    {showJerseyColumn && <th>Jersey</th>}
                    {showPositionColumn && <th>Position</th>}
                  </tr>
                </thead>

                <tbody>
                  {rosterEntries.map((r) => {
                    const team = r.seasons_leagues_team?.team;
                    const league =
                      r.seasons_leagues_team?.seasons_league?.league;
                    const season =
                      r.seasons_leagues_team?.seasons_league?.season;

                    return (
                      <tr key={r.id}>
                        <td>
                          {team && league && season ? (
                            <NavLink
                              className="playerIdentityAuth-link"
                              to={`/dashboard/seasons/${season.id}/leagues/${league.id}/teams/${team.id}`}
                            >
                              {team.name}
                            </NavLink>
                          ) : (
                            <span className="playerIdentityAuth-muted">
                              Unknown Team
                            </span>
                          )}
                        </td>

                        <td>
                          {season?.id ? (
                            <NavLink
                              className="playerIdentityAuth-link"
                              to={`/dashboard/seasons/${season.id}`}
                            >
                              {season.name || "Unknown Season"}
                            </NavLink>
                          ) : (
                            "Unknown Season"
                          )}
                        </td>

                        <td>
                          {season?.id && league?.id ? (
                            <NavLink
                              className="playerIdentityAuth-link"
                              to={`/dashboard/seasons/${season.id}/leagues/${league.id}`}
                            >
                              {league.name || "Unknown League"}
                            </NavLink>
                          ) : (
                            "Unknown League"
                          )}
                        </td>
                        <td>
                          {season?.id ? (
                            isAuthenticated ? (
                              <select
                                value={
                                  playerRegistrations[season.id] === undefined
                                    ? ""
                                    : String(playerRegistrations[season.id])
                                }
                                onChange={(e) =>
                                  handleRegistrationChange(
                                    season.id,
                                    e.target.value === "true"
                                  )
                                }
                                style={{
                                  color: playerRegistrations[season.id] === false
                                    ? "#dc2626"
                                    : "#16a34a",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                }}
                              >
                                <option value="" disabled>
                                  Loading...
                                </option>
                                <option value="true">Registered</option>
                                <option value="false">Unregistered</option>
                              </select>
                            ) : (
                              <span>
                                {playerRegistrations[season.id] === false
                                  ? "Unregistered"
                                  : "Registered"}
                              </span>
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                        {showJerseyColumn && <td>{r.jersey_number || "-"}</td>}
                        {showPositionColumn && <td>{r.position || "-"}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showIndividualHistory && (
          <div className="playerIdentityAuth-section">
            <div className="playerIdentityAuth-sectionHeader">
              <div>
                <h3>Individual League History</h3>
                <p>Singles or individual leagues where this player competes directly.</p>
              </div>

              <div className="playerIdentityAuth-countBox">
                <span>{individualEntries.length}</span>
                <small>Individual Entries</small>
              </div>
            </div>

            <div className="playerIdentityAuth-tableWrap">
              <table className="playerIdentityAuth-table playerIdentityAuth-tableSmall">
                <thead>
                  <tr>
                    <th>Season</th>
                    <th>League</th>
                  </tr>
                </thead>

                <tbody>
                  {individualEntries.map((entry) => {
                    const season = entry.seasons_league?.season;
                    const league = entry.seasons_league?.league;

                    return (
                      <tr key={entry.id}>
                        <td>
                          {season?.id ? (
                            <NavLink
                              className="playerIdentityAuth-link"
                              to={`/dashboard/seasons/${season.id}`}
                            >
                              {season.name || "Unknown Season"}
                            </NavLink>
                          ) : (
                            "Unknown Season"
                          )}
                        </td>

                        <td>
                          {season?.id && league?.id ? (
                            <NavLink
                              className="playerIdentityAuth-link"
                              to={`/dashboard/seasons/${season.id}/leagues/${league.id}`}
                            >
                              {league.name || "Unknown League"}
                            </NavLink>
                          ) : (
                            "Unknown League"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerAuth;